import "./style.scss";
import { FC, MouseEvent, useEffect, useState } from "react";
import ModalPortal from "@/src/components/ModalPortal/ModalPortal";
import EditorCloseSvg from "@/src/svg/applicationsPage/APPopUp/EditorCloseSvg";
import DocumentPreviewPlaceholder from "@/src/components/CustomUtils/DocumentPreviewPlaceholder/DocumentPreviewPlaceholder";
import DocumentCheckActionButtons from "@/src/components/CustomUtils/DocumentCheckActionButtons/DocumentCheckActionButtons";
import {
  buildPdfPreviewUrl,
  getDocumentPreviewKind,
  getDocumentTypeLabel,
  resolveFileExtension,
} from "@/src/utils/documentPreviewUtils";

export interface CustomDocumentPreviewModalProps {
  isOpen: boolean;
  documentUrl: string | null;
  onClose: () => void;
  onApprove?: () => void;
  onReject?: () => void;
  onToCorrect?: () => void;
  title?: string;
  fileExtension?: string;
  fileName?: string;
  /** When true, hide the close button and ignore backdrop clicks. Defaults to true if approve/reject actions are provided. */
  closeOnlyViaActions?: boolean;
  showCloseButton?: boolean;
  allowBackdropClose?: boolean;
}

const CustomDocumentPreviewModal: FC<CustomDocumentPreviewModalProps> = ({
  isOpen,
  documentUrl,
  onClose,
  onApprove,
  onReject,
  onToCorrect,
  title = "Document preview",
  fileExtension,
  fileName,
  closeOnlyViaActions,
  showCloseButton,
  allowBackdropClose,
}) => {
  const [imageFailed, setImageFailed] = useState(false);

  useEffect(() => {
    setImageFailed(false);
  }, [documentUrl, isOpen]);

  if (!isOpen || !documentUrl) return null;

  const extension = resolveFileExtension(fileExtension, fileName, documentUrl);
  const previewKind = getDocumentPreviewKind(extension);
  const typeLabel = getDocumentTypeLabel(extension);
  const showActions = Boolean(onApprove || onReject || onToCorrect);
  const shouldCloseOnlyViaActions = closeOnlyViaActions ?? showActions;
  const shouldShowCloseButton = showCloseButton ?? !shouldCloseOnlyViaActions;
  const shouldAllowBackdropClose = allowBackdropClose ?? !shouldCloseOnlyViaActions;
  const pdfPreviewUrl = previewKind === "pdf" ? buildPdfPreviewUrl(documentUrl) : documentUrl;

  const handleOverlayClick = (event: MouseEvent<HTMLDivElement>) => {
    if (!shouldAllowBackdropClose) return;
    if (event.target === event.currentTarget) {
      onClose();
    }
  };

  const renderPreviewContent = () => {
    if (previewKind === "image" && !imageFailed) {
      return (
        <img
          src={documentUrl}
          alt={title}
          className="custom-document-preview-modal-image"
          onError={() => setImageFailed(true)}
        />
      );
    }

    if (previewKind === "pdf") {
      return <iframe src={pdfPreviewUrl} className="custom-document-preview-modal-frame" title={title} />;
    }

    if (previewKind === "image" && imageFailed) {
      return <DocumentPreviewPlaceholder label="IMG" />;
    }

    return <DocumentPreviewPlaceholder label={typeLabel} />;
  };

  return (
    <ModalPortal>
      <div className="custom-document-preview-modal" onClick={handleOverlayClick}>
        {shouldShowCloseButton && (
          <button
            type="button"
            className="custom-document-preview-modal-close"
            onClick={(e) => {
              e.stopPropagation();
              onClose();
            }}
            aria-label="Close preview"
          >
            <EditorCloseSvg />
          </button>
        )}

        <div className="custom-document-preview-modal-content" onClick={(e) => e.stopPropagation()}>
          {renderPreviewContent()}
        </div>

        {showActions && (
          <div className="custom-document-preview-modal-actions" onClick={(e) => e.stopPropagation()}>
            <DocumentCheckActionButtons onApprove={onApprove} onReject={onReject} onToCorrect={onToCorrect} />
          </div>
        )}
      </div>
    </ModalPortal>
  );
};

export default CustomDocumentPreviewModal;
